import { PayloadAction } from "@reduxjs/toolkit";
import {
  call,
  put,
  select,
  takeEvery,
  takeLatest,
  takeLeading,
} from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import {
  ChassisHeightFilterOption,
  ChassisHeightForm,
  ChassisHeightFormDropDownKey,
  ChassisHeightListResponse,
  ChassisHeightDeletePayload,
  ChassisHeightDetailPayload,
  ChassisHeightDetailResponse,
  ChassisHeightRow,
  ChassisHeightUpdatePayload,
} from "../types";
import {
  selectChassisHeightForm,
  selectChassisHeightFormDropDowns,
  selectChassisHeightPage,
  selectChassisHeightPerPage,
} from "./selector";
import { chassisHeightActions } from "./slice";

let isFetchingList = false;

const fetchingFormDropDowns: Record<ChassisHeightFormDropDownKey, boolean> = {
  manufacturers: false,
  masterModels: false,
};

export function* handleGetChassisHeightList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectChassisHeightPage);
    const perPage: number = yield select(selectChassisHeightPerPage);

    const response: ChassisHeightListResponse = yield call(
      request,
      `${API_URL}/chassis-height/list?page=${page}&perPage=${perPage}`,
      { method: "GET" },
    );

    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { data, meta };

    if (page === 1) {
      yield put(chassisHeightActions.getChassisHeightListSuccess(payload));
    } else {
      yield put(chassisHeightActions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const page: number = yield select(selectChassisHeightPage);

    if (page === 1) {
      yield put(chassisHeightActions.getChassisHeightListError(message));
    } else {
      yield put(chassisHeightActions.getNextPageItemsError(message));
    }
  } finally {
    isFetchingList = false;
  }
}

export function* handleAddChassisHeight() {
  const form: ChassisHeightForm = yield select(selectChassisHeightForm);

  try {
    const response = yield call(request, `${API_URL}/chassis-height`, {
      method: "POST",
      body: JSON.stringify({
        manufacturerId: Number(form.manufacturer),
        modelId: Number(form.masterModel),
        hangerType: form.suspensionType.trim(),
        weelBase: Number(form.axleDistance),
        tire: form.tire.trim(),
        height: Number(form.net),
        year: Number(form.year),
        heightIncludeBox: Number(form.chassisHeightIncludingBody),
      }),
    });

    yield put(chassisHeightActions.addChassisHeightSuccess(response.data));
  } catch (error) {
    yield put(
      chassisHeightActions.addChassisHeightError((error as Error).message),
    );
  }
}

export function* handleGetFormDropDownList(
  action: PayloadAction<ChassisHeightFormDropDownKey>,
) {
  const key = action.payload;
  if (fetchingFormDropDowns[key]) return;
  fetchingFormDropDowns[key] = true;

  try {
    const formDropDowns = yield select(selectChassisHeightFormDropDowns);
    const { page, perPage } = formDropDowns[key];

    const url =
      key === "manufacturers"
        ? `${API_URL}/manufacturer?page=${page}&perPage=${perPage}`
        : `${API_URL}/super-model/list?page=${page}&perPage=${perPage}`;

    const response = yield call(request, url, { method: "GET" });
    const data = response?.data || [];

    const items =
      key === "manufacturers"
        ? data.map(
            (row: { menufecturerID: number; menufecturerName: string }) => ({
              id: row.menufecturerID,
              name: row.menufecturerName,
            }),
          )
        : data.map(
            (row: { superModelId: number; superModelName?: string }) => ({
              id: row.superModelId,
              name: row.superModelName || "",
            }),
          );

    const filtered = items.filter(
      (item: ChassisHeightFilterOption) => item.id && item.name,
    );
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { key, data: filtered, meta };

    if (page === 1) {
      yield put(chassisHeightActions.getFormDropDownListSuccess(payload));
    } else {
      yield put(chassisHeightActions.getFormDropDownNextPageSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const formDropDowns = yield select(selectChassisHeightFormDropDowns);

    if (formDropDowns[key].page === 1) {
      yield put(
        chassisHeightActions.getFormDropDownListError({ key, message }),
      );
    } else {
      yield put(
        chassisHeightActions.getFormDropDownNextPageError({ key, message }),
      );
    }
  } finally {
    fetchingFormDropDowns[key] = false;
  }
}

export function* handleUpdateChassisHeight(
  action: PayloadAction<ChassisHeightUpdatePayload>
) {
  const {
    manufacturerId,
    modelId,
    hangerType,
    weelBase,
    tire,
    height,
    year,
    heightIncludeBox,
    superModelId,
  } = action.payload;

  const query = new URLSearchParams({
    manufacturerId: String(manufacturerId),
    modelId: String(modelId),
    hangerType,
    weelBase: String(weelBase),
    tire,
  });

  try {
    yield call(
      request,
      `${API_URL}/chassis-height?manufacturerId=${manufacturerId}&modelId=${modelId}&hangerType=${encodeURIComponent(
        hangerType
      )}&weelBase=${weelBase}&tire=${encodeURIComponent(tire)}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          height: Number(height),
          year: Number(year),
          heightIncludeBox: Number(heightIncludeBox),
          superModelId: Number(superModelId),
        }),
      }
    );

    yield put(chassisHeightActions.updateChassisHeightSuccess());
    yield put(chassisHeightActions.getChassisHeightList());
  } catch (error) {
    yield put(
      chassisHeightActions.updateChassisHeightError((error as Error).message),
    );
  }
}

export function* handleDeleteChassisHeight(
  action: PayloadAction<ChassisHeightDeletePayload>
) {
  const { manufacturerId, modelId, hangerType, weelBase, tire } =
    action.payload;

  try {
    yield call(
      request,
      `${API_URL}/chassis-height?manufacturerId=${manufacturerId}&modelId=${modelId}&hangerType=${encodeURIComponent(
        hangerType
      )}&weelBase=${weelBase}&tire=${encodeURIComponent(tire)}`,
      { method: "DELETE" }
    );

    yield put(chassisHeightActions.deleteChassisHeightSuccess());
    yield put(chassisHeightActions.getChassisHeightList());
  } catch (error) {
    yield put(
      chassisHeightActions.deleteChassisHeightError((error as Error).message)
    );
  }
}

export function* handleGetChassisHeightDetail(
  action: PayloadAction<ChassisHeightDetailPayload>
) {
  const { manufacturerId, modelId, hangerType, weelBase, tire } = action.payload;

  try {
    const response: ChassisHeightDetailResponse = yield call(
      request,
      `${API_URL}/chassis-height/detail?manufacturerId=${manufacturerId}&modelId=${modelId}&hangerType=${encodeURIComponent(
        hangerType
      )}&weelBase=${weelBase}&tire=${encodeURIComponent(tire)}`,
      { method: "GET" },
    );

    const d: any = response?.data;

    const detail: ChassisHeightRow = {
      manufacturerId: Number(d?.manufacturerId),
      manufacturerName:
        typeof d?.manufacturerName === "string"
          ? d.manufacturerName
          : d?.manufacturerName?.description,
      modelId: Number(d?.modelId),
      hangerType: String(d?.hangerType ?? ""),
      weelBase: Number(d?.weelBase),
      tire: String(d?.tire ?? ""),
      height: d?.height !== undefined ? Number(d.height) : undefined,
      year: d?.year !== undefined ? Number(d.year) : undefined,
      heightIncludeBox:
        d?.heightIncludeBox !== undefined ? Number(d.heightIncludeBox) : undefined,
      superModelId:
        d?.superModelId !== undefined ? Number(d.superModelId) : undefined,
      superModelDesc:
        typeof d?.superModelDesc === "string"
          ? d.superModelDesc
          : d?.superModelDesc?.description,
    };

    yield put(chassisHeightActions.getChassisHeightDetailSuccess(detail));
  } catch (error) {
    yield put(
      chassisHeightActions.getChassisHeightDetailError(
        (error as Error).message
      )
    );
  }
}

export function* chassisHeightSaga() {
  yield takeLeading(
    [
      chassisHeightActions.getChassisHeightList.type,
      chassisHeightActions.getNextPageItems.type,
    ],
    handleGetChassisHeightList,
  );
  yield takeLatest(
    chassisHeightActions.addChassisHeight.type,
    handleAddChassisHeight,
  );
  yield takeLatest(
    chassisHeightActions.updateChassisHeight.type,
    handleUpdateChassisHeight,
  );
  yield takeLatest(
    chassisHeightActions.deleteChassisHeight.type,
    handleDeleteChassisHeight,
  );

  yield takeLatest(
    chassisHeightActions.getChassisHeightDetail.type,
    handleGetChassisHeightDetail,
  );

  yield takeEvery(
    chassisHeightActions.getFormDropDownList.type,
    handleGetFormDropDownList,
  );
  yield takeEvery(
    chassisHeightActions.getFormDropDownNextPage.type,
    handleGetFormDropDownList,
  );
}

export default chassisHeightSaga;
